import { dateLib, tenantLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import type { TenantRes } from "@agentcall/core/res"
import { Plus, User } from "react-feather"
import { Link, useNavigate, useParams } from "react-router-dom"
import { roomApi } from "@/api/client/room"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { RoomHeader } from "@/components/properties/RoomHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card"
import { Loading } from "@/components/ui/loading"

export function TenantsPage() {
  const navigate = useNavigate()
  const { propertyId: propertyIdParam, roomId: roomIdParam } = useParams()
  const propertyId = propertyIdParam ? Number(propertyIdParam) : undefined
  const roomId = roomIdParam ? Number(roomIdParam) : undefined
  const hasValidIds = propertyId !== undefined && roomId !== undefined

  // Fetch room data with property and tenants
  const {
    data: roomResponse,
    isLoading: isRoomLoading,
    error,
  } = roomApi.useRoomGet(
    roomId,
    { withProperty: "true", withTenants: "true" },
    {
      query: {
        enabled: hasValidIds,
      },
    }
  )

  const handleAddTenant = () => {
    if (propertyId !== undefined && roomId !== undefined) {
      navigate(`/properties/${propertyId}/rooms/${roomId}/tenants/add`)
    }
  }

  if (isRoomLoading) {
    return <Loading className="mt-10" text="テナント情報を読み込んでいます..." />
  }

  if (!hasValidIds || error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-slate-600">
          {hasValidIds
            ? "テナント情報の取得に失敗しました。"
            : "テナント情報が見つかりませんでした。"}
        </div>
        <Button onClick={() => navigate(-1)} variant="outline">
          前のページに戻る
        </Button>
      </div>
    )
  }

  const room = roomResponse?.room
  const tenants = room?.tenants ?? []

  if (!propertyId || !roomId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDまたは部屋IDが不正です。</div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PropertyHeader propertyId={propertyId} />
      <RoomHeader roomId={roomId} />

      <div className="flex justify-end">
        <Button onClick={handleAddTenant} size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          新規追加
        </Button>
      </div>

      {tenants.length === 0 ? (
        <Card className="p-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <User className="h-12 w-12 text-slate-300" />
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-600">テナントが登録されていません</h3>
              <p className="text-xs text-slate-500">新規追加ボタンからテナントを追加してください</p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {tenants.map((tenant) => (
            <Link
              key={tenant.id}
              to={`/properties/${propertyId}/rooms/${roomId}/tenants/${tenant.id}`}
            >
              <Card className="cursor-pointer transition-all hover:shadow-md">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-primary">{tenant.name}</h3>
                          <Badge variant={tenantLib.getStatusVariant(tenant as TenantRes)}>
                            {tenantLib.getStatusLabel(tenant as TenantRes)}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500">{tenant.nameKana}</p>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">契約開始日</span>
                        <span className="text-xs font-semibold text-slate-700">
                          {dateLib.formatDate(tenant.startDate)}
                        </span>
                      </div>
                      {tenant.endDate && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">契約終了日</span>
                          <span className="text-xs font-semibold text-slate-700">
                            {dateLib.formatDate(tenant.endDate)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">賃料</span>
                        <span className="text-xs font-semibold text-slate-700">
                          {tenant.rent.toLocaleString()}円
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">電話番号</span>
                        <span className="text-xs font-semibold text-slate-700">
                          {formatPhone(tenant.phone1, tenant.phone2, tenant.phone3)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
